import React, { useState, useMemo, useCallback } from 'react';
import TaskModal from '../components/forms/TaskModal';
import DismissRecommendationModal from '../components/recommendations/DismissRecommendationModal';
import {
  buildTaskPrefill,
  createTaskFromRecommendation,
  dismissRecommendation,
  snoozeRecommendation,
  completeRecommendation
} from '../utils/recommendations';

/**
 * Shared accept/dismiss/snooze/complete handling for the Recommendations tab and queue page.
 * Returns card handlers plus the modal elements to render.
 */
export function useRecommendationActions({ user, users = [], canAssignAny = false, onToast }) {
  const [busyId, setBusyId] = useState(null);
  const [acceptRec, setAcceptRec] = useState(null);
  const [dismissRec, setDismissRec] = useState(null);

  const toast = useCallback((message, type = 'success') => onToast?.({ message, type }), [onToast]);

  const run = useCallback(async (rec, fn, successMsg) => {
    setBusyId(rec.id);
    try {
      await fn();
      if (successMsg) toast(successMsg);
    } catch (err) {
      console.error('Recommendation action failed:', err);
      toast(`Action failed: ${err.message}`, 'error');
    } finally {
      setBusyId(null);
    }
  }, [toast]);

  const handlers = useMemo(() => ({
    onAccept: (rec) => setAcceptRec(rec),
    onDismiss: (rec) => setDismissRec(rec),
    onSnooze: (rec, days) => run(rec, () => snoozeRecommendation(rec, days, user), `Snoozed for ${days} days`),
    onComplete: (rec) => run(rec, () => completeRecommendation(rec, user), 'Marked as done')
  }), [run, user]);

  const prefill = useMemo(() => (acceptRec ? buildTaskPrefill(acceptRec, user) : null), [acceptRec, user]);
  const customersForModal = useMemo(
    () => (acceptRec ? [{ id: acceptRec.customerId, customerName: acceptRec.customerName }] : []),
    [acceptRec]
  );

  const modals = (
    <>
      {acceptRec && prefill && (
        <TaskModal
          salesReps={users.filter(u => u.uid)}
          initial={prefill}
          isAdmin={canAssignAny}
          customers={customersForModal}
          onClose={() => setAcceptRec(null)}
          onSave={async (taskData) => {
            const rec = acceptRec;
            setBusyId(rec.id);
            try {
              const taskId = await createTaskFromRecommendation(rec, taskData, user);
              setAcceptRec(null);
              toast(`Task ${taskId} created`);
            } catch (err) {
              console.error('Create task from recommendation failed:', err);
              toast(`Failed to create task: ${err.message}`, 'error');
              throw err;
            } finally {
              setBusyId(null);
            }
          }}
        />
      )}
      {dismissRec && (
        <DismissRecommendationModal
          rec={dismissRec}
          onClose={() => setDismissRec(null)}
          onConfirm={async (feedback) => {
            await dismissRecommendation(dismissRec, feedback, user);
            setDismissRec(null);
            toast('Recommendation dismissed — thanks for the feedback');
          }}
        />
      )}
    </>
  );

  return { busyId, handlers, modals };
}
